import { RotateCcw } from "lucide-react";
import { VelocityInput } from "./pitchInput/VelocityInput";
import { PitchGrid } from "./pitchInput/PitchGrid";
import { PitchResultGrid } from "./pitchInput/PitchResultGrid";
import { ZoneGrid } from "./pitchInput/ZoneGrid";
import { ScriptedPitchView } from "./pitchInput/ScriptedPitchView";
import { PITCH_TYPES, PITCH_RESULTS } from "./pitchInput/constants";

const PitchInput = ({
  currentPitch,
  onChange,
  onSubmit,
  onReset,
  disabled = false,
  isBullpen = false,
  isScripted = false,
  scriptedPitch = null,
  zoneType = "standard",
}) => {
  const handleChange = (field, value) => {
    onChange({ ...currentPitch, [field]: value });
  };

  if (isScripted && scriptedPitch) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4 shadow-sm">
        <ScriptedPitchView
          scriptedPitch={scriptedPitch}
          currentPitch={currentPitch}
          onVelocityChange={(value) => handleChange("velocity", value)}
          onNoteChange={(value) => handleChange("note", value)}
          onSubmit={onSubmit}
          onReset={onReset}
          disabled={disabled}
        />
      </div>
    );
  }

  const handleReset = () => {
    onChange(
      isBullpen
        ? {
            velocity: null,
            type: "",
            intendedZone: null,
            location: null,
            note: "",
          }
        : {
            velocity: null,
            type: "",
            result: "",
            location: null,
            note: "",
          }
    );
    if (onReset) onReset();
  };

  const hasPitchLocation =
    currentPitch?.location?.x != null && currentPitch?.location?.y != null;
  const canSubmitBullpen =
    !!currentPitch?.type && !!currentPitch?.intendedZone && hasPitchLocation;
  const canSubmitGame =
    !!currentPitch?.type && !!currentPitch?.result && hasPitchLocation;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4 shadow-sm">
      <div className="flex justify-between items-center mb-3 sm:mb-4">
        <h2 className="text-base sm:text-lg font-semibold text-gray-900">
          Pitch Details
        </h2>
        <button
          onClick={handleReset}
          disabled={disabled}
          className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-1 text-xs sm:text-sm text-gray-600 bg-gray-100 
            rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed
            transition-colors"
        >
          <RotateCcw size={14} />
          <span>Reset</span>
        </button>
      </div>

      <div className="space-y-3 sm:space-y-4">
        {isBullpen ? (
          <>
            <div>
              <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1 block">
                Intended Zone
              </label>
              <ZoneGrid
                selected={currentPitch.intendedZone}
                onChange={(value) => handleChange("intendedZone", value)}
                disabled={disabled}
                zoneType={zoneType}
              />
            </div>
            <VelocityInput
              value={currentPitch.velocity}
              onChange={(value) => handleChange("velocity", value)}
              disabled={disabled}
            />
            <div>
              <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1 block">
                Pitch Type
              </label>
              <PitchGrid
                options={PITCH_TYPES}
                selected={currentPitch.type}
                onChange={(value) => handleChange("type", value)}
                disabled={disabled}
              />
            </div>
          </>
        ) : (
          <>
            <VelocityInput
              value={currentPitch.velocity}
              onChange={(value) => handleChange("velocity", value)}
              disabled={disabled}
            />
            <div>
              <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1 block">
                Pitch Type
              </label>
              <PitchGrid
                options={PITCH_TYPES}
                selected={currentPitch.type}
                onChange={(value) => handleChange("type", value)}
                disabled={disabled}
              />
            </div>
            <div>
              <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1 block">
                Result
              </label>
              <PitchResultGrid
                options={PITCH_RESULTS}
                selected={currentPitch.result}
                onChange={(value) => handleChange("result", value)}
                disabled={disabled}
              />
            </div>
          </>
        )}

        <div>
          <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1 block">
            Notes
          </label>
          <textarea
            value={currentPitch.note || ""}
            onChange={(e) => handleChange("note", e.target.value)}
            placeholder="Optional notes about the pitch..."
            className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-200 rounded-md text-xs sm:text-sm
              focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500
              resize-none"
            rows={2}
            disabled={disabled}
          />
        </div>

        <button
          onClick={onSubmit}
          disabled={disabled || (isBullpen ? !canSubmitBullpen : !canSubmitGame)}
          className="w-full py-1.5 sm:py-2 px-3 sm:px-4 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-md 
            hover:from-blue-700 hover:to-blue-600 focus:outline-none focus:ring-2 
            focus:ring-blue-500 focus:ring-offset-2 
            disabled:opacity-50 disabled:cursor-not-allowed 
            transition-all font-medium shadow-sm text-sm"
        >
          Add Pitch
        </button>
      </div>
    </div>
  );
};

export default PitchInput;
